import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './router-react/index';
import { ThemeLanguageProvider } from '@/contexts/ThemeLanguageContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { PlatformStatusProvider } from '@/contexts/PlatformStatusContext';
import { cerrarSiEsPopupDeCalendario } from '@/utils/oauthPopup';
import './styles/colors.css';
import './App.css';

// ==========================================
// DESHABILITAR LOGS DE CONSOLA GLOBALMENTE
// ==========================================
// Según requerimiento: "no queremos que se imprima nada en consola del navegador"
if (true) {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  console.error = noop; // Silenciar también errores para cumplir con "nada"
}

const rootElement = document.getElementById('app');

if (!rootElement) {
  throw new Error('Root element #app not found');
}

// Componente ErrorBoundary para capturar fallos en componentes lazy o post-render
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    // Error capturado por ErrorBoundary - solo loguear en desarrollo
    if (import.meta.env.DEV) {
      console.error('Error capturado por Root ErrorBoundary:', error, errorInfo);
    }
  }
  render() {
    if (this.state.hasError) {
      return React.createElement('div', {
        style: { padding: '40px', textAlign: 'center', fontFamily: 'sans-serif', background: '#fff' }
      },
        React.createElement('h1', null, 'Algo salió mal'),
        React.createElement('p', null, this.state.error?.message || 'La aplicación encontró un error inesperado.'),
        React.createElement('button', {
          onClick: () => window.location.reload(),
          style: { padding: '10px 20px', cursor: 'pointer', background: '#0069E0', color: '#fff', border: 'none', borderRadius: '4px' }
        }, 'Recargar página')
      );
    }
    return this.props.children;
  }
}

// Si esta ventana es la emergente del OAuth de calendario, ya avisó a la
// pestaña original y se está cerrando. Montar la aplicación aquí enseñaría el
// flujo de postulación dentro del popup durante un instante y, si el cierre
// fallara, dejaría dos copias compitiendo por el mismo localStorage.
const esPopupDeCalendario = cerrarSiEsPopupDeCalendario();

if (!esPopupDeCalendario) {
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      React.createElement(React.StrictMode, null,
        React.createElement(ErrorBoundary, null,
          React.createElement(ThemeLanguageProvider, null,
            React.createElement(PlatformStatusProvider, null,
              React.createElement(NotificationProvider, null,
                React.createElement(RouterProvider, { router })
              )
            )
          )
        )
      )
    );
  } catch (error) {
    console.error('Error rendering app:', error);
    rootElement.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif; background: #ffcccc;">
        <h1>Error al cargar la aplicación</h1>
        <p>${error instanceof Error ? error.message : 'Error desconocido'}</p>
        <pre>${error instanceof Error ? error.stack : JSON.stringify(error)}</pre>
        <p>Por favor, revisa la consola del navegador para más detalles.</p>
      </div>
    `;
  }
}
